import { useLayoutEffect, useRef, useState, type ChangeEvent, type ReactNode, type Ref } from 'react'
import { validateOutreachSignupReviewField, validateOutreachSignupStep, type CallTrustLevelKey } from '@tenfour/contracts'
import type { SignupReviewField } from './scribeFields'
import { RequiredMark } from '@/components/auth/AuthShell'
import { useStreamedText } from './useStreamedText'
import { AccentButton, SignupIdentityArt, SignupLogo } from './visuals'
import c from './conversation.module.css'
import { useTurnEntrance, useExitTransition } from './useTurnMotion'
import { ControlOptions } from './ControlOptions'
export { ControlOptions }
import motion from './PromptResponse.module.css'
import { AnswerHistory, AnswerInput, questionFor } from './AnswerHistory'
import { useThreadPosition } from './useThreadPosition'

export function StreamedText({ text, className }: { text: string; className?: string }) {
  const { visible } = useStreamedText(text)
  return <span className={className}><span aria-hidden="true">{visible}</span><span className={c.srOnly}>{text}</span></span>
}

export function SignupConversation({ title, history, onBack, children, transitionRef, stickyTitle = false }: {
  title: string; transitionRef?: Ref<HTMLDivElement>; history?: ReactNode; onBack: () => void; children: ReactNode; stickyTitle?: boolean
}) {
  const heading = useRef<HTMLDivElement>(null)
  const ready = useTurnEntrance(heading, title)
  const { visible, done } = useStreamedText(title, ready)
  const position = useThreadPosition()
  return <div className={c.page} ref={transitionRef}>
    <header className={c.header}><button type="button" className={c.headerBack} onClick={onBack}><span aria-hidden="true">←</span> Back</button><SignupLogo />{position.away && <button type="button" className={c.latest} onClick={position.latest}>Current question <span aria-hidden="true">↓</span></button>}</header>
    <main ref={position.thread} className={`${c.thread} ${motion.thread} ${stickyTitle ? c.businessThread : ''}`}>
      {history}
      <div ref={heading} className={motion.turn} data-turn-body data-turn-phase={ready ? 'typing' : 'scrolling'} tabIndex={-1}>
      <h1 className={`${c.prompt} ${motion.textSlot}`} aria-label={title} data-current-question><span className={motion.measure} aria-hidden="true">{title}</span><span className={motion.typed} aria-hidden="true">{visible}</span></h1>
      {done && <div className={`${c.content} ${motion.response}`}>{children}</div>}
      </div>
      <div ref={position.end} className={c.threadEnd} aria-hidden="true" />
    </main>
  </div>
}

export function BusinessQuestions({ fields, values, onChange, onContinue, errorKey, index, onIndexChange }: {
  fields: SignupReviewField[]; values: Record<string, string>; onChange: (key: string, value: string) => void
  onContinue: () => void; errorKey?: string; index: number; onIndexChange: (index: number) => void
}) {
  const [localError, setError] = useState('')
  const field = fields[Math.min(index, fields.length - 1)]
  const value = field ? values[field.key] ?? '' : ''
  const error = localError || (field && field.key === errorKey ? validateOutreachSignupReviewField(field, value)?.message ?? '' : '')
  const prompt = field ? questionFor(field) : 'Your business details are ready.'
  const current = useRef<HTMLFormElement>(null)
  const ready = useTurnEntrance(current, prompt)
  const { visible, done } = useStreamedText(prompt, ready)
  const response = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => { if (response.current) response.current.inert = !done }, [done])
  const transition = useExitTransition()
  function change(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setError('')
    if (field) onChange(field.key, event.target.value)
  }
  function next() {
    const issue = field ? validateOutreachSignupReviewField(field, value) : null
    if (issue) { setError(issue.message); document.getElementById('business-answer')?.focus(); return }
    setError('')
    if (index + 1 < fields.length) { void transition.advance(() => onIndexChange(index + 1)); return }
    const [reviewIssue] = validateOutreachSignupStep('review', { reviewFields: fields, fieldValues: values }, { blank: false })
    if (reviewIssue) {
      onIndexChange(Math.max(0, fields.findIndex((item) => item.key === reviewIssue.field)))
      setError(reviewIssue.message)
      return
    }
    onContinue()
  }
  function submit(event: React.FormEvent) { event.preventDefault(); next() }
  return <div className={c.questions} ref={transition.ref} data-business-questions>
    <AnswerHistory fields={fields.slice(0, index)} values={values} onChange={onChange} />
    <form onSubmit={submit} ref={current} className={`${c.currentTurn} ${motion.businessTurn}`} data-turn-phase={ready ? 'typing' : 'scrolling'} tabIndex={-1} data-current-question>
      <p className={c.questionProgress}>Your business <span>{Math.min(index + 1, fields.length)} of {fields.length}</span></p>
      <label htmlFor="business-answer" className={`${c.question} ${motion.textSlot}`} aria-label={prompt}><span className={motion.measure} aria-hidden="true">{prompt}</span><span className={motion.typed} aria-hidden="true">{visible}</span>{field?.required && <RequiredMark />}</label>
      {done && field?.hint && <div id="business-hint"><StreamedText text={field.hint} className={c.support} /></div>}
      <div ref={response} className={done ? motion.response : motion.pending} data-response-state={done ? 'ready' : 'waiting'} aria-hidden={!done}>
      {field && <div>
        <AnswerInput field={field} value={value} onChange={change} id="business-answer" error={error} focus={done} describedBy={[field.hint ? 'business-hint' : '', error ? 'business-error' : ''].filter(Boolean).join(' ') || undefined} />
      </div>}
      {error && <p role="alert" id="business-error" className={c.error}>{error}</p>}
      <div className={c.actions}><AccentButton type="submit" disabled={!done}>Continue</AccentButton><span className={c.historyHint}>You can edit your answers above.</span></div>
      </div>
    </form>
  </div>
}

export function WorkspaceOptions({ value, onChange, onContinue }: {
  value: 'solo' | 'team' | null; onChange: (value: 'solo' | 'team') => void; onContinue: () => void
}) {
  function choose(event: React.MouseEvent<HTMLButtonElement>) { onChange(event.currentTarget.value as 'solo' | 'team') }
  return <>
    <div className={c.options} role="group" aria-label="Who will use Ten Four?">
      <button type="button" value="solo" onClick={choose} aria-pressed={value === 'solo'} className={c.option}><SignupIdentityArt kind="solo" /><strong><StreamedText text="Just me" /></strong><StreamedText text="One person running the business." /></button>
      <button type="button" value="team" onClick={choose} aria-pressed={value === 'team'} className={c.option}><SignupIdentityArt kind="team" /><strong><StreamedText text="My team" /></strong><StreamedText text="A shared workspace for your crew." /></button>
    </div>
    <div className={c.actions}><AccentButton disabled={!value} onClick={onContinue}>Continue</AccentButton></div>
  </>
}
